"use client";

import { useEffect } from "react";

/**
 * The browser half of the error sink (TRY-418) — one hook, used by all three client
 * error boundaries.
 *
 * All three already render `error.digest` as "ref: …", so the correlation handle was
 * already on the customer's screen; what was missing was any server-side record of
 * it. This posts to `/api/report-error`, which hands it to the same `reportError`
 * the server legs use, so one reader sees both.
 *
 * ## Deliberately provider-free and dependency-free
 *
 * Every one of the three boundaries carries a comment saying it uses no `next-intl`
 * hooks "so it can never fail itself" — `global-error.tsx` goes further and renders
 * its own `<html>` with inline styles because `globals.css` may not have loaded. A
 * reporting hook that could throw would defeat that: it would turn a handled error
 * screen into a blank one. So this uses nothing but `useEffect` and `fetch`, and its
 * `catch` is empty on purpose.
 *
 * ⚠️ **It must never surface a failure to the user.** There is nothing useful to say
 * — the page is already an error screen — and a second failure notice on top of the
 * first is worse than silence. The server's own floor (`console.error` inside
 * `reportError`) is where a failure to record is visible.
 *
 * ## It does NOT capture to Sentry itself, and the reason is measured
 *
 * It used to call `Sentry.captureException` here as well as POSTing, on the
 * grounds that a boundary-caught render error never reaches the browser SDK's
 * global handlers. Both statements are true; filing both was still wrong, because
 * the two legs land in Sentry as two unrelated issues for one event —
 * `SyntaxError: Unterminated string in JSON` (JANA-NEXTJS-G, 3 events, tagged
 * `boundary: global`) and `ClientBoundaryError: Unterminated string in JSON`
 * (JANA-NEXTJS-H, 3 events, `scope: client/global`) were the same three failures
 * counted twice under two names.
 *
 * ⚠️ **The POST is the leg that kept, and the browser capture is the one that
 * dropped events** — the opposite of the intuition, which is why it is written
 * down. Measured over 7 days:
 *
 *   boundary   POST leg                    browser capture       kept
 *   global     JANA-NEXTJS-H, 3 events     JANA-NEXTJS-G, 3      both
 *   global     JANA-NEXTJS-J, 2 events     JANA-NEXTJS-K, 1      POST
 *   root       JANA-NEXTJS-F, 15 events    none at all           POST
 *
 * `keepalive` is the whole difference: a chunk-load failure is followed by a
 * reload, which kills an in-flight Sentry envelope and does not touch a keepalive
 * fetch. The `root` row is 15 of the 23 client events in that window, so keeping
 * the browser leg and dropping the POST would have lost most of them.
 *
 * ⚠️ And the browser leg carries no better a stack **today**: source maps upload
 * only when `SENTRY_AUTH_TOKEN` is set at build time (`next.config.ts`), and
 * JANA-NEXTJS-G's frames arrived minified, so the `stack` string forwarded in the
 * POST body is the same text. If map upload is ever turned on, that changes and
 * this decision is worth revisiting — capture here and skip the server-side
 * capture for `source: "client-boundary"` instead. Do not simply add this one
 * back: that is the double-filing above.
 *
 * The SDK's global handlers are untouched, so an ordinary uncaught error or
 * rejection still reaches Sentry from the browser on its own.
 *
 * ## Fires once per distinct error, not once per mount
 *
 * React re-invokes effects on a StrictMode double-render and on every re-render that
 * changes the deps, and a boundary can re-render while the user reads it. A
 * module-level `Set` of keys is the dedup: without it a single failure posts two or
 * more identical reports, which makes the sink's own counts useless for judging how
 * often something is actually breaking. `keepalive` is set so a report survives the
 * user immediately navigating away from the error screen.
 */
const reported = new Set<string>();

/**
 * Set `explained` when the boundary has IDENTIFIED this failure and is rendering
 * a specific answer for it, rather than "something went wrong".
 *
 * ⚠️ **An explained failure must not also be reported** (JANA-NEXTJS-1A,
 * JANA-NEXTJS-17). A stale tab posting a server action the new build no longer
 * has is the case that exists today: `useStaleDeploymentError` reads it off
 * Next's own `UnrecognizedActionError`, the screen says Jana was updated and
 * nothing was saved, and the remedy is a button the user is already looking at.
 * Filing that is filing a redeploy as a defect, and it arrives on the release
 * that CAUSED it, so it reads as a regression in whatever shipped.
 *
 * ⚠️ It is the boundary's verdict and not a message test, because the message is
 * the one thing that cannot be trusted here: in production Next redacts a server
 * error's message to a constant. See `lib/app-update-boundary.ts`.
 *
 * ⚠️ This is NOT a place to put "errors we would rather not see". The sink is
 * the only durable record there is. An entry belongs here when the app already
 * told the user something true and actionable about this exact failure, and
 * nowhere else.
 */
export function useReportBoundaryError(
	boundary: "global" | "root" | "panel",
	error: Error & { digest?: string },
	options?: { explained?: boolean },
): void {
	const explained = options?.explained ?? false;
	useEffect(() => {
		if (explained) return;
		const key = `${boundary}|${error.digest ?? ""}|${error.message}`;
		if (reported.has(key)) return;
		reported.add(key);

		try {
			// An AbortController wired to this effect's cleanup would abort the report
			// the moment the boundary unmounts, which is precisely the case `keepalive`
			// exists to cover: a user who reads the error screen and immediately
			// navigates away is the one whose report matters most. Nothing reads the
			// response, so there is no post-unmount state update to leak.
			// (The retired `react/web-api-no-leaked-fetch` directive is gone with the
			// antfu stack; the note above is the standing argument for why this
			// fetch stays un-aborted.)
			void fetch("/api/report-error", {
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					boundary,
					message: error.message,
					digest: error.digest,
					stack: error.stack,
					location: globalThis.location?.href,
				}),
				keepalive: true,
			}).catch(() => {});
		} catch {
			// See the header: a failure to report must never become a second failure
			// on a screen that is already telling the user something went wrong.
		}
	}, [boundary, error, explained]);
}
